if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.connected = false;
      this.scrollFrame = null;
      this.pickerFocusTimer = null;
      this.intersectionObserver = null;
      this.resizeObserver = null;

      this.intersectionHandler = this.handleIntersection.bind(this);
      this.scrollHandler = this.handleScrollEvent.bind(this);
      this.pageShowHandler = this.handlePageShow.bind(this);
      this.formChangeHandler = this.handleProductFormChange.bind(this);
      this.formSubmitHandler = this.handleFormSubmit.bind(this);
      this.flushScrollHandler = this.flushScroll.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;

      this.resolveElements();
      if ('IntersectionObserver' in window) this.bindEvents();
      this.bindForm();
      this.bindVariantSync();
    }

    disconnectedCallback() {
      this.connected = false;
      this.unbindEvents();

      if (this.productForm) {
        this.productForm.removeEventListener('change', this.formChangeHandler);
      }
      if (this.form) this.form.removeEventListener('submit', this.formSubmitHandler);
      if (this.pickerFocusTimer !== null) {
        window.clearTimeout(this.pickerFocusTimer);
        this.pickerFocusTimer = null;
      }
    }

    resolveElements() {
      this.productSection = this.closest('.cc-main-product') || document.querySelector('.cc-main-product');
      this.productForm = (this.productSection ? this.productSection.querySelector('product-form') : null) || document.querySelector('product-form');
      this.form = this.querySelector('[data-sticky-atc-form]');
      this.idInput = this.querySelector('[data-sticky-atc-id]');
      this.button = this.querySelector('.sticky-atc-panel__btn');
    }

    bindEvents() {
      this.intersectionObserver = new IntersectionObserver(this.intersectionHandler);
      if (this.productForm) this.intersectionObserver.observe(this.productForm);

      const footer = document.querySelector('.cc-footer');
      if (footer && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scrollHandler);
        this.resizeObserver.observe(footer);
      }

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      window.addEventListener('pageshow', this.pageShowHandler);
      StickyAtcPanel.handleScroll();
    }

    unbindEvents() {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('pageshow', this.pageShowHandler);

      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.intersectionObserver = null;
      this.resizeObserver = null;

      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.target === this.productForm) {
          this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
        }
      });
    }

    handlePageShow(evt) {
      if (!evt.persisted || !this.connected) return;

      this.handleScrollEvent();
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        if (this.productForm) this.intersectionObserver.observe(this.productForm);
      }
    }

    handleScrollEvent() {
      if (!this.connected || this.scrollFrame !== null) return;
      this.scrollFrame = window.requestAnimationFrame(this.flushScrollHandler);
    }

    flushScroll() {
      this.scrollFrame = null;
      if (this.connected) StickyAtcPanel.handleScroll();
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }

    // -------------------------------------------------------------
    // Synchronise l'ID de variante avec le form principal
    // (changement de taille / couleur dans la buy box → sticky reflète)
    // -------------------------------------------------------------
    bindVariantSync() {
      if (!this.productForm || !this.idInput) return;
      this.productForm.removeEventListener('change', this.formChangeHandler);
      this.productForm.addEventListener('change', this.formChangeHandler);
    }

    handleProductFormChange() {
      // Synchronise la quantité du form principal → la barre ajoute la même quantité.
      const mainQty = this.productForm.querySelector('input[name="quantity"]');
      const stickyQty = this.querySelector('input[name="quantity"]');
      if (mainQty && stickyQty && mainQty.value) stickyQty.value = mainQty.value;

      const mainIdInput = this.productForm.querySelector('input[name="id"]');
      if (!mainIdInput || !mainIdInput.value) return;
      if (this.idInput.value !== mainIdInput.value) {
        this.idInput.value = mainIdInput.value;
        this.dataset.needsPicker = 'false';
        if (this.button) this.button.removeAttribute('data-needs-picker');
      }
    }

    // -------------------------------------------------------------
    // Submit AJAX vers /cart/add.js
    // -------------------------------------------------------------
    bindForm() {
      if (!this.form || !this.button) return;
      this.form.removeEventListener('submit', this.formSubmitHandler);
      this.form.addEventListener('submit', this.formSubmitHandler);
    }

    handleFormSubmit(e) {
      e.preventDefault();

      // Si pas de variante sélectionnée → scroll vers le picker + focus
      if (this.dataset.needsPicker === 'true' || this.button.dataset.needsPicker === 'true') {
        this.scrollToPicker();
        return;
      }

      if (this.button.classList.contains('is-loading') || this.button.classList.contains('is-success')) return;
      this.submitAdd();
    }

    scrollToPicker() {
      const picker = document.getElementById('variants') || document.querySelector('.option-selector');
      if (!picker) return;
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = picker.querySelector('input:not([disabled])');
      if (firstInput) {
        if (this.pickerFocusTimer !== null) window.clearTimeout(this.pickerFocusTimer);
        this.pickerFocusTimer = window.setTimeout(() => {
          this.pickerFocusTimer = null;
          if (this.connected) firstInput.focus();
        }, 400);
      }
    }

    async submitAdd() {
      const labelEl = this.button.querySelector('.sticky-atc-panel__btn-label');
      const originalLabel = labelEl ? labelEl.textContent : '';
      this.button.classList.add('is-loading');
      const formData = new FormData(this.form);
      const bundleItems = this.getSelectedBundleItems();
      const mainLineItem = this.buildLineItemFromFormData(formData);
      const useBundlePayload = bundleItems.length > 0 && mainLineItem;

      try {
        const fetchOptions = useBundlePayload
          ? {
              method: 'POST',
              body: JSON.stringify({
                items: [
                  mainLineItem,
                  ...bundleItems
                ],
                sections: 'cart-icon-bubble',
                sections_url: window.location.pathname
              }),
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }
          : {
              method: 'POST',
              body: formData,
              headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
            };

        const res = await fetch(this.form.action, fetchOptions);
        const data = await res.clone().json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.description || data.message || `HTTP ${res.status}`);
        }
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        if (labelEl) labelEl.textContent = this.dataset.successText || 'Added';

        if (useBundlePayload) {
          const detail = { source: 'sticky-atc-bundle', response: data };
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
          document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
          window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
          window.dispatchEvent(new CustomEvent('ocu:cart:open'));
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        setTimeout(() => {
          this.button.classList.remove('is-success');
          if (labelEl) labelEl.textContent = originalLabel;
        }, 1800);
      } catch (err) {
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-error');
        if (labelEl) labelEl.textContent = this.dataset.errorText || 'Error';
        // eslint-disable-next-line no-console
        console.error('[sticky-atc] add failed:', err);
        setTimeout(() => {
          this.button.classList.remove('is-error');
          if (labelEl) labelEl.textContent = originalLabel;
        }, 2200);
      }
    }

    getSelectedBundleItems() {
      const bundle = this.productForm ? this.productForm.querySelector('bb-bundle') : document.querySelector('bb-bundle');
      if (!bundle) return [];

      if (typeof bundle.getSelectedItems === 'function') {
        const methodItems = StickyAtcPanel.normalizeLineItems(bundle.getSelectedItems());
        const hasCheckedRows = Boolean(bundle.querySelector('[data-bb-bundle-checkbox]:checked:not(:disabled)'));
        if (methodItems.length > 0 || !hasCheckedRows) return methodItems;
      }

      const rows = Array.from(bundle.querySelectorAll('[data-bb-bundle-item]'));
      return StickyAtcPanel.normalizeLineItems(rows.reduce((items, row) => {
        const checkbox = row.querySelector('[data-bb-bundle-checkbox]');
        if (!checkbox || !checkbox.checked || checkbox.disabled) return items;

        const select = row.querySelector('[data-bb-bundle-select]');
        const selectedOption = select?.selectedOptions?.[0] || null;
        const variantId = parseInt(select?.value || checkbox.dataset.variantId, 10);
        const isAvailable = selectedOption ? selectedOption.dataset.available !== 'false' : true;

        if (variantId && isAvailable) {
          items.push({ id: variantId, quantity: 1 });
        }

        return items;
      }, []));
    }

    static normalizeLineItems(items) {
      if (!Array.isArray(items)) return [];

      return items.reduce((normalized, item) => {
        const id = parseInt(item.id, 10);
        const quantity = Math.max(parseInt(item.quantity, 10) || 1, 1);
        if (id) normalized.push({ id, quantity });
        return normalized;
      }, []);
    }

    buildLineItemFromFormData(formData) {
      const id = parseInt(formData.get('id'), 10);
      if (!id) return null;

      const quantity = Math.max(parseInt(formData.get('quantity'), 10) || 1, 1);
      const lineItem = { id, quantity };
      const sellingPlan = formData.get('selling_plan');
      const properties = {};

      if (sellingPlan) lineItem.selling_plan = sellingPlan;

      formData.forEach((value, key) => {
        if (!key.startsWith('properties[') || value === '') return;

        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
      });

      if (Object.keys(properties).length > 0) {
        lineItem.properties = properties;
      }

      return lineItem;
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
